import { API_ENDPOINTS } from '@/api/endpoints';
import { useOrg } from '@/organization/useOrg';
import { type BuildingAlternative } from '@/stores/SubprojectStore';
import { apiFetch } from '@/util/apiClient';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { darken } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import { DecarbOverviewTile } from '../DecarbOverviewTile';

const FONT_HEADER = 'Montserrat, sans-serif';

const USAGE_TO_ALTERNATIVE: Record<string, string> = {
  Conventional: 'conventional',
  'Low Carbon': 'reduced-carbon',
  'Net Zero': 'zero-carbon',
};

interface TileData {
  usage: string;
  alternatives: BuildingAlternative[];
  compares: BuildingAlternative[];
}

interface BoApprovalModalProps {
  open: boolean;
  onClose: () => void;
  tiles: TileData[];
  getColor: (index: number) => string;
  currentApproval?: string | null;
  projectId: string;
}

export function BoApprovalModal({
  open,
  onClose,
  tiles,
  getColor,
  currentApproval,
  projectId,
}: BoApprovalModalProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { selectedOrg } = useOrg();

  if (!open) return null;

  const handleTileClick = async (usage: string) => {
    if (!selectedOrg?.id) return;

    const alternative = USAGE_TO_ALTERNATIVE[usage];
    const newValue = alternative === currentApproval ? null : alternative;

    try {
      if (newValue) {
        await apiFetch(
          API_ENDPOINTS.ORGANIZATIONS.PROJECTS.BO_APPROVAL(
            selectedOrg.id,
            projectId,
          ),
          { method: 'POST', body: JSON.stringify({ alternative: newValue }) },
        );
      } else {
        await apiFetch(
          API_ENDPOINTS.ORGANIZATIONS.PROJECTS.BO_APPROVAL(
            selectedOrg.id,
            projectId,
          ),
          { method: 'DELETE' },
        );
      }
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({
        queryKey: ['projects-with-subprojects'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['project', selectedOrg.id, projectId],
      });
    } catch (error) {
      console.error('Failed to set BO approval:', error);
    }

    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Box
      onClick={handleBackdropClick}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        bgcolor: alpha(darken(theme.palette.primary.main, 0.6), 0.92),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: '95%',
          height: '70%',
          bgcolor: 'background.default',
          borderRadius: 2,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            bgcolor: 'info.main',
            borderRadius: 1,
            px: 4,
            py: 4,
            mb: 2,
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_HEADER,
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'common.black',
            }}
          >
            Select Budget-Approved Decarbonization Approach
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Grid container columnSpacing={2} sx={{ height: '100%' }}>
            {tiles.map((tile) => {
              const alt = USAGE_TO_ALTERNATIVE[tile.usage];
              const isSelected = alt === currentApproval;

              return (
                <Grid
                  key={tile.usage}
                  size={{ md: 4, sm: 12 }}
                  sx={{ height: '100%' }}
                >
                  <Box
                    onClick={() => handleTileClick(tile.usage)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1,
                      height: '100%',
                      border: isSelected
                        ? `3px solid white`
                        : '3px solid transparent',
                      transition: 'border-color 0.15s',
                      position: 'relative',
                      '&:hover': {
                        borderColor: 'white',
                      },
                      '&:hover::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        bgcolor: alpha(theme.palette.primary.main, 0.3),
                        borderRadius: 'inherit',
                        pointerEvents: 'none',
                      },
                    }}
                  >
                    <DecarbOverviewTile
                      usage={tile.usage}
                      getColor={getColor}
                      alternatives={tile.alternatives}
                      compares={tile.compares}
                      pmPreferred={false}
                      accent={isSelected}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
